import ComicSansMSFont from '@/assets/fonts/ComicSansMS-base64'
import { avifToPngBase64, getImagePath, HOST_URL_IMGS, loadImageBase64 } from './constantes'

export interface OpcionPDF {
  numero: number
  nombre: string
  descripcion: string
  selectedItems: any[]
  subtotal: number
  discountAmount: number
  totalWithDiscount: number
}

export interface PdfData {
  nombreCliente: string
  tipoEvento: string
  catalogoServicios: any[]
  eventDate: string
  guestCount: number
  discountPercent: number
  discountAmount: number
  subtotal: number
  totalWithDiscount: number
  payments: Array<{ amount: number }>
  pendingAmount: number
  observations: string
  selectedItems: any[]
  backgroundRespage: string
  backgroundHeader: string
  signatureImage: string
  mostrarImagenes?: boolean
  opciones?: OpcionPDF[]
}

/**
 * Formatea un número como moneda colombiana
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0
  }).format(value)
}

/**
 * Formatea un número con separadores de miles
 */
function formatNumber(value: number): string {
  return new Intl.NumberFormat('es-CO').format(value)
}

/**
 * Genera un PDF de cotización con los datos proporcionados
 * Soporta tanto cotizaciones simples como cotizaciones con múltiples opciones
 */
export async function generatePDFOptions(data: PdfData): Promise<void> {
  try {
    const { jsPDF } = (globalThis as any).jspdf
    const doc = new jsPDF()

    doc.addFileToVFS('@/assets/fonts/ComicSansMS.ttf', ComicSansMSFont)
    doc.addFont('@/assets/fonts/ComicSansMS.ttf', 'ComicSansMS', 'normal')

    const pageWidth = doc.internal.pageSize.width
    const pageHeight = doc.internal.pageSize.height
    
    const topMargin = 35
    const bottomMargin = 20

    // Función helper para agregar el fondo
    const addBackgroundToCurrentPage = (image: string) => {
      doc.addImage(image, 'PNG', 0, 0, pageWidth, pageHeight)
    }

    // ====== PORTADA ======
    addBackgroundToCurrentPage(data.backgroundHeader)

    // ====== PÁGINA DE CONTENIDO ======
    doc.addPage()
    addBackgroundToCurrentPage(data.backgroundRespage)

    // Colores corporativos
    const primaryColor = [223, 165, 163]
    const secondaryColor = [102, 126, 234]
    const darkGray = [51, 51, 51]
    const lightGray = [245, 245, 245]
    const black = [0, 0, 0]
    const white = [255, 255, 255]

    let yPos = 25

    // HEADER - Fecha y Tipo de Evento
    doc.setFontSize(10)
    doc.setFont('ComicSansMS', 'normal')
    const fechaTexto = new Date(data.eventDate).toLocaleDateString('es-CO', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
    doc.text(fechaTexto, 20, yPos)
    
    if (data.tipoEvento) {
      doc.setFontSize(9)
      doc.text(`Tipo de Evento: ${data.tipoEvento}`, 20, yPos + 5)
      yPos += 5
    }
    yPos += 15

    // Título principal
    doc.setFontSize(14)
    doc.setTextColor(...black)
    doc.setFont('ComicSansMS', 'normal')
    doc.text(`Queridos clientes, reciban un cordial saludo.`, 20, yPos)
    yPos += 8

    // Subtítulo 1
    doc.setFontSize(12)
    const introText = `A continuación, les presento la propuesta formal de nuestros servicios, donde ofrecemos alternativas cuidadosamente diseñadas para crear el ambiente ideal que deseas proyectar, garantizando una experiencia memorable.`
    const splitIntro = doc.splitTextToSize(introText, 170)
    doc.text(splitIntro, 20, yPos)
    yPos += splitIntro.length * 6 + 5

    // Subtítulo 2
    const introText2 = `Agradecemos la confianza que depositan en eventos Bibiana Díaz al considerar nuestro acompañamiento para un día tan especial.`
    const splitIntro2 = doc.splitTextToSize(introText2, 170)
    doc.text(splitIntro2, 20, yPos)
    yPos += splitIntro2.length * 6 + 10

    // ====== ORGANIZAR ITEMS POR JERARQUÍA (3 NIVELES) ======
    interface ItemsPorServicio {
      [servicioNombre: string]: {
        secciones: {
          [seccionNombre: string]: any[]
        }
      }
    }

    const itemsPorServicio: ItemsPorServicio = {}

    // Agrupar items por Servicio > Sección
    data.selectedItems.forEach(item => {
      const servicioNombre = item.NombreServicio || 'Sin Servicio'
      const seccionNombre = item.NombreSeccion || 'Sin Sección'

      if (!itemsPorServicio[servicioNombre]) {
        itemsPorServicio[servicioNombre] = { secciones: {} }
      }

      if (!itemsPorServicio[servicioNombre].secciones[seccionNombre]) {
        itemsPorServicio[servicioNombre].secciones[seccionNombre] = []
      }

      itemsPorServicio[servicioNombre].secciones[seccionNombre].push(item)
    })

    // ====== VERIFICAR SI HAY OPCIONES MÚLTIPLES ======
    const tieneOpciones = data.opciones && data.opciones.length > 0
 
    if (tieneOpciones) {
      // ====== MODO OPCIONES MÚLTIPLES ======
      
      // Información sobre las opciones
      if (data.opciones && data.opciones.length > 1) {
        doc.setFontSize(11)
        doc.setTextColor(...darkGray)
        const textoOpciones = `Esta propuesta está dividida en ${data.opciones!.length} ${data.opciones!.length > 1 ? 'opciones' : 'opción'}:`
        doc.text(textoOpciones, 20, yPos)
        yPos += 8
      }

      // Listar las opciones
      doc.setFontSize(10)
      data.opciones!.forEach((opcion, index) => {
 
        doc.text(`${index + 1}. ${opcion.nombre}`, 25, yPos)
        yPos += 6
      })
      yPos += 8

      // Texto informativo
      if (false) {
        doc.setFontSize(9)
        doc.setTextColor(...darkGray)
        const textoInfo = `Ambas opciones incluyen como obsequio el servicio de Wedding and Event Planner por la contratación de la decoración.`
        const splitTextoInfo = doc.splitTextToSize(textoInfo, 170)
        doc.text(splitTextoInfo, 20, yPos)
        yPos += splitTextoInfo.length * 5 + 15
      }

      // ====== RENDERIZAR CADA OPCIÓN ======
      for (let i = 0; i < data.opciones!.length; i++) {
        const opcion = data.opciones?.[i]
        if (opcion?.selectedItems.length === 0) continue
        // Verificar espacio para el título de la opción
        if (yPos + 20 > pageHeight - bottomMargin) {
          doc.addPage()
          addBackgroundToCurrentPage(data.backgroundRespage)
          yPos = topMargin
        }

        // Título de la opción
        doc.setFontSize(16)
        // doc.setFillColor(...primaryColor)
        // doc.rect(15, yPos - 5, 180, 10, 'F')
        // doc.setTextColor(...white)
        doc.setFont('ComicSansMS', 'normal')
        // doc.text(opcion?.nombre.toUpperCase(), 20, yPos + 2)
        // centrar texto
        doc.text(opcion?.nombre.toUpperCase(), pageWidth / 2, yPos + 2, { align: 'center' })
        yPos += 15

        // Descripción si existe
        if (opcion?.descripcion) {
          doc.setFontSize(10)
          doc.setTextColor(...darkGray)
          const splitDesc = doc.splitTextToSize(opcion.descripcion, 170)
          doc.text(splitDesc, 20, yPos)
          yPos += splitDesc.length * 5 + 8
        }

        // Organizar items por jerarquía
        const itemsPorServicio = organizarItemsPorJerarquia(opcion?.selectedItems || [])
        // Renderizar items de la opción
        for (const [servicioNombre, servicioData] of Object.entries(itemsPorServicio)) {
          doc.setFillColor(...primaryColor)
          doc.setDrawColor(...primaryColor)
          doc.rect(15, yPos - 5, 180, 8, 'F')
          doc.setFontSize(12)
          doc.setTextColor(...white)
          doc.setFont('ComicSansMS', 'normal')
          doc.text(servicioNombre.toUpperCase(), 20, yPos)
          yPos += 10
          for (const [seccionNombre, items] of Object.entries((servicioData as any).secciones)) {
            // Verificar espacio
            if (yPos + 15 > pageHeight - bottomMargin) {
              doc.addPage()
              addBackgroundToCurrentPage(data.backgroundRespage)
              yPos = topMargin
            }

            // Título de la sección
            doc.setFontSize(12)
            doc.setTextColor(...black)
            doc.setFont('ComicSansMS', 'normal')
            doc.text(`◆ ${seccionNombre.toUpperCase()}`, 20, yPos)
            yPos += 8

            // Items de la sección
            doc.setFontSize(9)
            doc.setTextColor(...darkGray)
            
            for (const item of items as any[]) {
              // Verificar espacio para el item
              if (yPos + 6 > pageHeight - bottomMargin) {
                doc.addPage()
                addBackgroundToCurrentPage(data.backgroundRespage)
                yPos = topMargin
              }

              const cantidad = item.Cantidad && item.Cantidad > 1 ? `${item.Cantidad}x ` : ''
              const itemText = `• ${cantidad}${item.NombreItem}`
              const splitItem = doc.splitTextToSize(itemText, 165)
              doc.text(splitItem, 25, yPos)
              yPos += splitItem.length * 5
            }

            yPos += 5 // Espacio entre secciones

            // Renderizar imágenes de la sección si están disponibles
            yPos = await renderImagenesSeccion(
              items as any[], 
              doc, 
              yPos, 
              pageHeight, 
              bottomMargin, 
              topMargin, 
              data.backgroundRespage, 
              addBackgroundToCurrentPage
            )
          }
        }

        yPos += 10 // Espacio entre opciones
      }

      // ====== RESUMEN DE INVERSIÓN PARA CADA OPCIÓN ======
      doc.addPage()
      addBackgroundToCurrentPage(data.backgroundRespage)
      yPos = topMargin

      doc.setFontSize(16)
      doc.setFillColor(...primaryColor)
      doc.rect(15, yPos - 5, 180, 10, 'F')
      doc.setTextColor(...white)
      doc.text('INVERSIÓN POR OPCIÓN', pageWidth / 2, yPos + 2, { align: 'center' })
      yPos += 20

      // Crear tabla comparativa
      const tableData: any[] = []
      
      data.opciones!.forEach(opcion => {
        tableData.push([
          { content: opcion.nombre, styles: { fontStyle: 'bold', fontSize: 11 } },
          ''
        ])
        tableData.push(['Subtotal', formatCurrency(opcion.subtotal)])
        if (data.discountPercent > 0) {
          tableData.push([
            `Descuento (${data.discountPercent}%)`,
            `-${formatCurrency(opcion.discountAmount)}`
          ])
        }
        tableData.push([
          { content: 'INVERSIÓN TOTAL', styles: { fontStyle: 'bold' } },
          { content: formatCurrency(opcion.totalWithDiscount), styles: { fontStyle: 'bold', fontSize: 12 } }
        ])
        tableData.push(['', '']) // Fila vacía de separación
      })

      doc.autoTable({
        startY: yPos,
        head: [['CONCEPTO', 'VALOR']],
        body: tableData,
        theme: 'grid',
        headStyles: {
          fillColor: primaryColor,
          textColor: white,
          fontSize: 11,
          fontStyle: 'bold',
          halign: 'center',
          font: 'ComicSansMS'
        },
        bodyStyles: {
          fontSize: 10,
          textColor: black,
          font: 'ComicSansMS'
        },
        columnStyles: {
          0: { cellWidth: 120 },
          1: { cellWidth: 60, halign: 'right', fontStyle: 'bold' }
        },
        alternateRowStyles: {
          fillColor: lightGray
        },
        didParseCell: function(cellData: any) {
          if (cellData.cell.raw === '') {
            cellData.cell.styles.fillColor = white
            cellData.cell.styles.minCellHeight = 3
          }
        },
        didDrawPage: function(pageData: any) {
          if (pageData.pageNumber > 1) {
            addBackgroundToCurrentPage(data.backgroundRespage)
          }
        }
      })

      // Nota informativa
      yPos = (doc as any).lastAutoTable.finalY + 15
      
      if (yPos + 20 > pageHeight - bottomMargin) {
        doc.addPage()
        addBackgroundToCurrentPage(data.backgroundRespage)
        yPos = topMargin
      }

      doc.setFontSize(9)
      doc.setTextColor(...darkGray)
      const notaText = `ESTA COTIZACIÓN ES REALIZADA PARA ${data.guestCount} ASISTENTES. EN CASO DE AUMENTAR O DISMINUIR EL NÚMERO DE INVITADOS, SE AJUSTARÁ EL COSTO FINAL.`
      const splitNota = doc.splitTextToSize(notaText, 170)
      doc.text(splitNota, 20, yPos)

    } else {
      // ====== MODO COTIZACIÓN SIMPLE (SIN OPCIONES) ======
      // Usar la lógica original del PDF
      
      const itemsPorServicio = organizarItemsPorJerarquia(data.selectedItems)

      for (const [servicioNombre, servicioData] of Object.entries(itemsPorServicio)) {
        if (yPos + 15 > pageHeight - bottomMargin) {
          doc.addPage()
          addBackgroundToCurrentPage(data.backgroundRespage)
          yPos = topMargin
        }

        doc.setFontSize(13)
        doc.setFillColor(...primaryColor)
        doc.rect(15, yPos - 5, 180, 8, 'F')
        doc.setTextColor(...white)
        doc.setFont('ComicSansMS', 'normal')
        doc.text(servicioNombre, 20, yPos)
        yPos += 10

        for (const [seccionNombre, items] of Object.entries((servicioData as any).secciones)) {
          if (yPos + 10 > pageHeight - bottomMargin) {
            doc.addPage()
            addBackgroundToCurrentPage(data.backgroundRespage)
            yPos = topMargin
          }

          doc.setFontSize(11)
          doc.setTextColor(...secondaryColor)
          doc.setFont('ComicSansMS', 'normal')
          doc.text(`◆ ${seccionNombre}`, 20, yPos)
          yPos += 7

          doc.setFontSize(9)
          doc.setTextColor(...darkGray)
          
          for (const item of items as any[]) {
            if (yPos + 6 > pageHeight - bottomMargin) {
              doc.addPage()
              addBackgroundToCurrentPage(data.backgroundRespage)
              yPos = topMargin
            }

            const cantidad = item.Cantidad && item.Cantidad > 1 ? `${item.Cantidad}x ` : ''
            const itemText = `• ${cantidad}${item.NombreItem}`
            const splitItem = doc.splitTextToSize(itemText, 165)
            doc.text(splitItem, 25, yPos)
            yPos += splitItem.length * 5
          }

          yPos += 5

          yPos = await renderImagenesSeccion(
            items as any[], 
            doc, 
            yPos, 
            pageHeight, 
            bottomMargin, 
            topMargin, 
            data.backgroundRespage, 
            addBackgroundToCurrentPage
          )
        }

        yPos += 10
      }

      // Tabla de resumen simple
      const tableData: any[] = []
      tableData.push(['Subtotal', formatCurrency(data.subtotal)])
      
      if (data.discountPercent > 0) {
        tableData.push([`Descuento (${data.discountPercent}%)`, `-${formatCurrency(data.discountAmount)}`])
      }

      tableData.push(['Total con Descuento', formatCurrency(data.totalWithDiscount)])
      
      let totalAbonos = 0
      data.payments.forEach((payment: any, idx: number) => {
        const monto = payment.Monto ? Number(payment.Monto) : payment.amount
        if (monto > 0) {
          totalAbonos += monto
          tableData.push([`Abono ${idx + 1}`, `$ ${formatNumber(monto)}`])
        }
      })

      tableData.push(['TOTAL ABONADO', `$ ${formatNumber(totalAbonos)}`])

      if (totalAbonos > 0) {
        tableData.push(['', ''])
      }

      tableData.push(['SALDO PENDIENTE', formatCurrency(data.pendingAmount)])

      const estimatedTableHeight = (tableData.length * 8) + 25
      
      if (yPos + estimatedTableHeight > pageHeight - bottomMargin) {
        doc.addPage()
        addBackgroundToCurrentPage(data.backgroundRespage)
        yPos = topMargin
      } else {
        yPos += 10
      }

      doc.setFontSize(14)
      doc.setFillColor(...primaryColor)
      doc.rect(15, yPos - 5, 180, 8, 'F')
      doc.setTextColor(...white)
      doc.text('RESUMEN DE SERVICIOS', 20, yPos)
      yPos += 10

      const subtotalIndex = tableData.findIndex(row => row[0] === 'Subtotal')
      const descuentoIndex = tableData.findIndex(row => row[0].includes('Descuento'))
      const totalIndex = tableData.findIndex(row => row[0] === 'Total con Descuento')
      const saldoIndex = tableData.findIndex(row => row[0] === 'SALDO PENDIENTE')

      doc.autoTable({
        startY: yPos,
        head: [['CONCEPTO', 'VALOR']],
        body: tableData,
        theme: 'grid',
        headStyles: {
          fillColor: primaryColor,
          textColor: black,
          fontSize: 11,
          fontStyle: 'bold',
          halign: 'center',
          font: 'ComicSansMS'
        },
        bodyStyles: {
          fontSize: 10,
          textColor: black,
          font: 'ComicSansMS'
        },
        columnStyles: {
          0: { cellWidth: 120 },
          1: { cellWidth: 60, halign: 'right', fontStyle: 'bold' }
        },
        alternateRowStyles: {
          fillColor: lightGray
        },
        didParseCell: function(cellData: any) {
          const rowIndex = cellData.row.index

          if (cellData.cell.raw === '') {
            cellData.cell.styles.fillColor = white
            cellData.cell.styles.minCellHeight = 3
          }

          if (rowIndex < subtotalIndex && cellData.cell.raw !== '') {
            cellData.cell.styles.fillColor = [250, 250, 250]
            cellData.cell.styles.fontStyle = 'bold'
          }

          if (rowIndex === subtotalIndex) {
            cellData.cell.styles.fillColor = [240, 240, 240]
            cellData.cell.styles.fontStyle = 'bold'
            cellData.cell.styles.fontSize = 11
          }

          if (rowIndex === descuentoIndex && data.discountPercent > 0) {
            cellData.cell.styles.fillColor = [255, 240, 240]
            cellData.cell.styles.textColor = [200, 0, 0]
            cellData.cell.styles.fontStyle = 'bold'
          }

          if (rowIndex === totalIndex) {
            cellData.cell.styles.fillColor = primaryColor
            cellData.cell.styles.textColor = white
            cellData.cell.styles.fontSize = 12
            cellData.cell.styles.fontStyle = 'bold'
          }

          if (cellData.cell.raw && cellData.cell.raw.toString().includes('Abono')) {
            cellData.cell.styles.fillColor = [240, 255, 240]
            cellData.cell.styles.textColor = [0, 150, 0]
          }

        if (cellData.cell.raw && cellData.cell.raw.toString().includes('Total Abonado')) {
          cellData.cell.styles.fillColor = [240, 255, 240]
          cellData.cell.styles.textColor = [0, 150, 0]  
          cellData.cell.styles.fontSize = 12 
          cellData.cell.styles.valign = 'middle'
          cellData.cell.styles.minCellHeight = 8 
          cellData.cell.styles.fontStyle = 'bold'
        }

          if (rowIndex === saldoIndex) {
            cellData.cell.styles.fillColor = [255, 243, 205]
            cellData.cell.styles.textColor = [255, 152, 0]
            cellData.cell.styles.fontSize = 12
            cellData.cell.styles.fontStyle = 'bold'
          }
        },
        didDrawPage: function(pageData: any) {
          if (pageData.pageNumber > 2) {
            addBackgroundToCurrentPage(data.backgroundRespage)
          }
        }
      })
    }

    // ====== PÁGINA DE TÉRMINOS Y CONDICIONES ======
    doc.addPage()
    addBackgroundToCurrentPage(data.backgroundRespage)

    doc.setFontSize(19)
    doc.setTextColor(60, 60, 60)
    doc.setFont('ComicSansMS', 'normal')
    doc.text('FORMA DE PAGO', pageWidth / 2, 40, { align: 'center' })

    doc.setFontSize(18)
    const leftMargin = 10
    const rightMargin = 10
    const textWidth = pageWidth - leftMargin - rightMargin

    const text1 = 'Realizar un primer abono para reservar la fecha y cerrar contrato, los posteriores pagos se realizarán según acuerdo de pago.'
    const splitText1 = doc.splitTextToSize(text1, textWidth)
    doc.text(splitText1, leftMargin, 58, { align: 'justify', maxWidth: textWidth })

    const text2a = 'La boda debe estar 100% cancelada un mes antes del gran día.'
    const splitText2a = doc.splitTextToSize(text2a, textWidth)
    doc.text(splitText2a, leftMargin, 85, { align: 'justify', maxWidth: textWidth })

    const text2b = 'Otros eventos deben estar pagados en su totalidad 1 semana antes del evento.'
    const splitText2b = doc.splitTextToSize(text2b, textWidth)
    doc.text(splitText2b, leftMargin, 100, { align: 'justify', maxWidth: textWidth })

    const text3 = 'Consignar a la cuenta de ahorros Bancolombia: #17596443559 A nombre de Bibiana Diaz Arteaga (C.C 1047411141) Una vez realizada la transacción envíenos el comprobante para enviarle el recibido.'
    const splitText3 = doc.splitTextToSize(text3, textWidth)
    doc.text(splitText3, leftMargin, 130, { align: 'justify', maxWidth: textWidth })

    const text4 = 'Me encuentro a su disposición para atender hasta el más mínimo detalle, y brindarle toda la asesoría necesaria para alcanzar el éxito que usted desea.'
    const splitText4 = doc.splitTextToSize(text4, textWidth)
    doc.text(splitText4, leftMargin, 170, { align: 'justify', maxWidth: textWidth })

    const text5 = 'De la manera más atenta y presta a resolver cualquier inquietud o sugerencia que tengan.'
    const splitText5 = doc.splitTextToSize(text5, textWidth)
    doc.text(splitText5, leftMargin, 200, { align: 'justify', maxWidth: textWidth })

    // Firma
    if (data.signatureImage) {
      doc.addImage(data.signatureImage, 'PNG', 15, pageHeight - 55, 60, 20)
    }

    // Guardar PDF
    const fileName = `Cotizacion_${data.nombreCliente.replace(/\s+/g, '_')}_${data.tipoEvento ? data.tipoEvento.replace(/\s+/g, '_') + '_' : ''}${new Date().toISOString().split('T')[0]}.pdf`
    doc.save(fileName)

    return Promise.resolve()
  } catch (error) {
    console.error('Error generando PDF:', error)
    return Promise.reject(error)
  }
}

/**
 * Organiza items por jerarquía (Servicio > Sección)
 */
function organizarItemsPorJerarquia(items: any[]): any {
  const resultado: any = {}

  items.forEach(item => {
    const servicioNombre = item.NombreServicio || 'Sin Servicio'
    const seccionNombre = item.NombreSeccion || 'Sin Sección'

    if (!resultado[servicioNombre]) {
      resultado[servicioNombre] = { secciones: {} }
    }

    if (!resultado[servicioNombre].secciones[seccionNombre]) {
      resultado[servicioNombre].secciones[seccionNombre] = []
    }

    resultado[servicioNombre].secciones[seccionNombre].push(item)
  })

  return resultado
}

/**
 * Renderiza imágenes de una sección (MEJORADO - ARREGLA EL PROBLEMA DE POSICIONAMIENTO)
 */
async function renderImagenesSeccion(
  seccion: any[],
  doc: any,
  currentYPos: number,
  pageHeight: number,
  bottomMargin: number,
  topMargin: number,
  backgroundImage: string,
  addBackgroundFn: any
): Promise<number> {
  // Recolectar todas las imágenes únicas de la sección
  const todasLasImagenes: string[] = []
  const imagenesSet = new Set<string>()
  
  seccion.forEach((item: any) => {
    if (item.ImagenesSeccion && item.ImagenesSeccion.length > 0) {
      try {
        const imagenes = Array.isArray(item.ImagenesSeccion) 
          ? item.ImagenesSeccion 
          : JSON.parse(item.ImagenesSeccion || '[]')
        
        imagenes.forEach((img: string) => {
          if (!imagenesSet.has(img)) {
            imagenesSet.add(img)
            todasLasImagenes.push(img)
          }
        })
      } catch (e) {
        console.error('Error parseando ImagenesSeccion:', e)
      }
    }
  })

  // Si no hay imágenes, retornar la posición actual
  if (todasLasImagenes.length === 0) {
    return currentYPos
  }

  // Configuración de diseño de imágenes
  
  const imgWidth = 40 + 30       // Ancho de cada imagen
  const imgHeight = 40  // Alto de cada imagen
  const imgSpacing = 5       // Espacio entre imágenes
  const imgsPerRow = 2       // Imágenes por fila
  const leftMargin = 20      // Margen izquierdo
  
  // Calcular cuántas filas necesitamos
  const totalRows = Math.ceil(todasLasImagenes.length / imgsPerRow)
  const totalImagesHeight = (totalRows * imgHeight) + ((totalRows - 1) * imgSpacing)
  
  // Verificar si hay espacio suficiente en la página actual
  if (currentYPos + totalImagesHeight + 10 > pageHeight - bottomMargin) {
    // No hay espacio, crear nueva página
    doc.addPage()
    addBackgroundFn(backgroundImage)
    currentYPos = topMargin
  } else {
    // Hay espacio, agregar un pequeño margen antes de las imágenes
    currentYPos += 8
  }

  // let yPos = currentYPos
  let imgCount = 0
  
  // Renderizar las imágenes
  for (const imagenUrl of todasLasImagenes) {
    // Calcular posición en la fila
    const col = imgCount % imgsPerRow
    const row = Math.floor(imgCount / imgsPerRow)
    
    // Calcular coordenadas X e Y
    const xPos = leftMargin + (col * (imgWidth + imgSpacing))
    const yPos = currentYPos + (row * (imgHeight + imgSpacing))
    
    
    try {
    // Cargar y agregar la imagen
    // si la url es base 64 no usar getImagePath
    let url = imagenUrl
    let base64 = ''
    if (!imagenUrl.startsWith('data:image')) {
      url = getImagePath(imagenUrl)
      base64 = await loadImageBase64(url)
    } else {
      if (imagenUrl.includes('avif')) {
        base64 = await avifToPngBase64(imagenUrl)
      } else {
        base64 = imagenUrl
      }
    }
      
      // Agregar la imagen al PDF
      doc.addImage(base64, xPos, yPos, imgWidth, imgHeight)
      
      // Agregar borde a la imagen
      doc.setDrawColor(200, 200, 200)
      doc.setLineWidth(0.3)
      doc.rect(xPos, yPos, imgWidth, imgHeight)
      
    } catch (error) {
      console.error('Error cargando imagen:', imagenUrl, error)
      
      // Dibujar un placeholder si la imagen no carga
      doc.setFillColor(240, 240, 240)
      doc.rect(xPos, yPos, imgWidth, imgHeight, 'F')
      doc.setDrawColor(200, 200, 200)
      doc.rect(xPos, yPos, imgWidth, imgHeight)
      
      // Texto de error pequeño
      doc.setFontSize(6)
      doc.setTextColor(150, 150, 150)
      doc.text('Error', xPos + imgWidth/2, yPos + imgHeight/2, { align: 'center' })
    }
    
    imgCount++
  }
  
  // Retornar la nueva posición Y después de todas las imágenes
  return currentYPos + totalImagesHeight + 10
}
 